import { useFormContext } from 'react-hook-form';
import deconstructLocation from '../../../../utils/deconstructLocation';
import { KEYWORD_INPUT_ID } from '../../constants';
import { useSearchContext } from '../../context/Search';
import useGoTo from '../../common/hooks/useGoTo';
import findUrl from '../../../../utils/findUrl';
import slugify from '../../../../utils/slugify';

/**
 * Returns the path to navigate to. If they keyword is an exact match to the top suggested category (ignoring casing),
 * then go to the top category's path. Otherwise use the `/search?where={}&what={}` pattern.
 * @param {string} args.keyword Keyword input field value
 * @param {Object} args.selectedSearchArea The user's selected search area
 * @param {Object} args.topCategorySuggestion The top category suggestion
 * @returns {string} The path to use
 */
const getPath = ({
  keyword,
  selectedSearchArea,
  topCategorySuggestion,
  topProfileSuggestion,
}) => {
  const trimmedKeyword = (keyword || '').trim();
  const topCategorySuggestionName = topCategorySuggestion
    ? topCategorySuggestion.name
    : '';
  const keywordMatchesTopCategory =
    trimmedKeyword.toLowerCase() === topCategorySuggestionName.toLowerCase();
  if (keywordMatchesTopCategory) {
    return findUrl({
      keywordPath: topCategorySuggestion.path,
      suburb_url: selectedSearchArea.path,
    });
  }
  const topProfileSuggestionName = topProfileSuggestion
    ? topProfileSuggestion.name
    : '';
  const keywordMatchesTopProfile =
    trimmedKeyword.toLowerCase() === topProfileSuggestionName.toLowerCase();
  if (keywordMatchesTopProfile) {
    return topProfileSuggestion.path;
  }
  const { name } = selectedSearchArea;
  const { state, postcode } = deconstructLocation(selectedSearchArea.path);
  return `/search?where=${slugify(name)},${state}${postcode &&
    `,${postcode}`}&what=${slugify(trimmedKeyword)}`;
};

const useSearchFormLogic = () => {
  const {
    categorySuggest: { topCategorySuggestion },
    profileSuggest: { profileSuggestions },
    modal: [, setShowModal],
    selectedSearchArea: [selectedSearchArea],
  } = useSearchContext();
  const { handleSubmit: handleSubmitWrapper } = useFormContext();
  const goTo = useGoTo();

  /**
   * On form submit, go to the search page if keyword and search area has been provided. If the keyword exactly matches
   * the top category suggestion then go to the top category suggestion's path, otherwise use the `/search?` path.
   * @param {Object} data Data from react-hook-form
   */
  const handleSubmit = data => {
    const keyword = data[KEYWORD_INPUT_ID];
    if (keyword && selectedSearchArea) {
      const path = getPath({
        keyword,
        selectedSearchArea,
        topCategorySuggestion,
        topProfileSuggestion:
          profileSuggestions && profileSuggestions.length
            ? profileSuggestions[0]
            : null,
      });
      goTo({
        keyword,
        path,
        selectedSearchArea,
      });
      setShowModal(false);
    }
  };

  return { handleSubmit: handleSubmitWrapper(handleSubmit) };
};

export default useSearchFormLogic;
